import { useEffect, useRef, useState } from "react";

type UseAnimatedUnmountOptions = {
    /**
     * Duration of the animation in milliseconds
     */
    duration: number;
    /**
     * Whether the element should be hidden
     */
    isHidden: boolean;
    /**
     * Callback that fires after the animation completes
     */
    onAnimationComplete?: () => void;
};

/**
 * A hook that handles animating an element before unmounting it from the DOM.
 * This helps prevent layout shifts and provides smooth transitions.
 *
 * @example
 * ```tsx
 * const { shouldRender } = useAnimatedUnmount({
 *   duration: 300,
 *   isHidden: hideElement
 * });
 *
 * return shouldRender ? <div className={isHidden ? "opacity-0" : "opacity-100"}>Content</div> : null;
 * ```
 */
export const useAnimatedUnmount = ({ duration, isHidden, onAnimationComplete }: UseAnimatedUnmountOptions) => {
    const [shouldRender, setShouldRender] = useState(!isHidden);
    const [isAnimating, setIsAnimating] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        // Clear any existing timeout to prevent stale animations
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsAnimating(true);

        if (isHidden) {
            // When hiding, wait for animation to complete
            timeoutRef.current = setTimeout(() => {
                setShouldRender(false);
                setIsAnimating(false);
                onAnimationComplete?.();
            }, duration);
        } else {
            // When showing, render immediately
            setShouldRender(true);
            // Still wait for animation to complete before setting isAnimating to false
            timeoutRef.current = setTimeout(() => {
                setIsAnimating(false);
                onAnimationComplete?.();
            }, duration);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isHidden, duration, onAnimationComplete]);

    return {
        shouldRender,
        isAnimating,
    };
};
